import React, { useState } from "react";
import { PackagePlus, Send, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_CLS = { open: "st-parts", ordered: "st-in_progress", delivered: "st-completed", cancelled: "st-paused" };

/* Mechanic: "auto X heeft Y nodig" → reception/warehouse gets a bell notification. */
export const PartsRequestPanel = ({ card, t, busy, locked, onRequest, onSetStatus, office }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [qty, setQty] = useState(1);
  const [urgent, setUrgent] = useState(false);
  const reqs = card.parts_requests || [];
  const submit = async () => { await onRequest({ text: text.trim(), quantity: Number(qty) || 1, urgent }); setText(""); setQty(1); setUrgent(false); setOpen(false); };
  return (
    <div className="space-y-2" data-testid="parts-request-panel">
      {!locked && onRequest && (
        <Button variant="outline" className="w-full h-12 rounded-xl border-orange-500/50 text-orange-600 hover:bg-orange-500/10" disabled={busy} onClick={() => setOpen(true)} data-testid="parts-request-open"><PackagePlus className="h-4 w-4 mr-2" />{t("partsRequestBtn")}</Button>
      )}
      {reqs.length > 0 && (
        <div className="rounded-xl border border-border divide-y divide-border" data-testid="parts-request-list">
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              {r.urgent && <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" />}
              <span className="font-mono text-xs text-muted-foreground">{r.quantity}×</span>
              <span className="flex-1 truncate font-medium">{r.text}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide ${STATUS_CLS[r.status]}`} data-testid={`parts-request-status-${r.id}`}>{t(`partsReq_${r.status}`)}</span>
              {office && onSetStatus && r.status === "open" && <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={() => onSetStatus(r.id, "ordered")} data-testid={`parts-request-ordered-${r.id}`}>{t("partsReq_markOrdered")}</Button>}
              {office && onSetStatus && (r.status === "open" || r.status === "ordered") && <Button size="sm" className="h-7 text-xs rounded-full" onClick={() => onSetStatus(r.id, "delivered")} data-testid={`parts-request-delivered-${r.id}`}>{t("partsReq_markDelivered")}</Button>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="parts-request-dialog">
          <DialogHeader><DialogTitle>{t("partsRequestTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("partsRequestHint")}</p>
          <Input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={t("partsRequestPh")} className="h-12 text-base" data-testid="parts-request-text" onKeyDown={(e) => e.key === "Enter" && text.trim() && submit()} />
          <div className="flex items-center gap-3">
            <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 w-24 text-base text-center" data-testid="parts-request-qty" />
            <button type="button" onClick={() => setUrgent((v) => !v)} className={`h-12 px-4 rounded-xl border text-sm font-semibold flex items-center gap-2 ${urgent ? "bg-rose-500 text-white border-rose-500" : "border-border"}`} data-testid="parts-request-urgent"><AlertTriangle className="h-4 w-4" />{t("partsRequestUrgent")}</button>
          </div>
          <Button className="h-12 rounded-full" disabled={!text.trim() || busy} onClick={submit} data-testid="parts-request-submit"><Send className="h-4 w-4 mr-2" />{t("partsRequestSend")}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
